class SteganoCodeCore {
    static MAGIC = '0101001101000011'; // 'SC'

    static textToBinary(text: string) {
        return text.split('').map(char => {
            return char.charCodeAt(0).toString(2).padStart(8, '0');
        }).join('');
    }

    static binaryToText(binary: string) {
        let text = '';
        for (let i = 0; i < binary.length; i += 8) {
            const byte = binary.substr(i, 8);
            if (byte.length === 8) {
                text += String.fromCharCode(parseInt(byte, 2));
            }
        }
        return text;
    }

    static async sha256(message: string) {
        const msgBuffer = new TextEncoder().encode(message);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    static async encodeImage(imageFile: File, data: string, password = ''): Promise<Blob> {
        return new Promise(async (resolve, reject) => {
            try {
                const img = new Image();
                const reader = new FileReader();

                reader.onload = async (e: any) => {
                    img.onload = async () => {
                        try {
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d')!;
                            canvas.width = img.width;
                            canvas.height = img.height;
                            
                            ctx.drawImage(img, 0, 0);
                            
                            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                            const pixels = imageData.data;

                            let binaryData = data;
                            if (password) {
                                const passwordHash = await this.sha256(password);
                                const hashBinary = this.textToBinary(passwordHash);
                                binaryData = hashBinary + data;
                            }

                            const lengthBinary = (binaryData.length >>> 0).toString(2).padStart(32, '0');
                            const finalData = lengthBinary + binaryData;

                            const maxCapacity = pixels.length / 4 * 3;
                            if (finalData.length > maxCapacity) {
                                reject(new Error('Image too small for this amount of data.'));
                                return;
                            }

                            let dataIndex = 0;
                            for (let i = 0; i < pixels.length; i += 4) {
                                for (let j = 0; j < 3; j++) {
                                    if (dataIndex < finalData.length) {
                                        pixels[i + j] = (pixels[i + j] & 0xFE) | parseInt(finalData[dataIndex]);
                                        dataIndex++;
                                    }
                                }
                            }

                            ctx.putImageData(imageData, 0, 0);

                            canvas.toBlob((blob) => {
                                if (blob) resolve(blob);
                                else reject(new Error('Blob creation failed'));
                            }, 'image/png');

                        } catch (err) {
                            reject(err);
                        }
                    };
                    img.src = e.target.result;
                };

                reader.onerror = () => reject(new Error('Failed to read image file'));
                reader.readAsDataURL(imageFile);

            } catch (err) {
                reject(err);
            }
        });
    }

    static async decodeImage(imageFile: File, password = ''): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const img = new Image();
                const reader = new FileReader();

                reader.onload = async (e: any) => {
                    img.onload = async () => {
                        try {
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d')!;
                            canvas.width = img.width;
                            canvas.height = img.height;
                            
                            ctx.drawImage(img, 0, 0);
                            
                            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                            const pixels = imageData.data;

                            let bits = '';
                            let bitIndex = 0;
                            while (bits.length < 32 && bitIndex < pixels.length / 4 * 3) {
                                const pixelIdx = Math.floor(bitIndex / 3) * 4;
                                const colorIdx = bitIndex % 3;
                                bits += (pixels[pixelIdx + colorIdx] & 1).toString();
                                bitIndex++;
                            }

                            const totalPayloadBits = parseInt(bits, 2);
                            if (isNaN(totalPayloadBits) || totalPayloadBits <= 0 || totalPayloadBits > pixels.length / 4 * 3) {
                                reject(new Error('No hidden data found or image is corrupted.'));
                                return;
                            }

                            let extractedData = '';
                            while (extractedData.length < totalPayloadBits && bitIndex < pixels.length / 4 * 3) {
                                const pixelIdx = Math.floor(bitIndex / 3) * 4;
                                const colorIdx = bitIndex % 3;
                                extractedData += (pixels[pixelIdx + colorIdx] & 1).toString();
                                bitIndex++;
                            }

                            if (extractedData.length < totalPayloadBits) {
                                reject(new Error('Incomplete data found in image.'));
                                return;
                            }

                            if (password) {
                                const passwordHash = await this.sha256(password);
                                const hashLength = passwordHash.length * 8;

                                if (extractedData.length < hashLength) {
                                    reject(new Error('Incorrect password or corrupted data'));
                                    return;
                                }

                                const storedHashBinary = extractedData.substring(0, hashLength);
                                const storedHash = this.binaryToText(storedHashBinary);

                                if (storedHash !== passwordHash) {
                                    reject(new Error('Incorrect password'));
                                    return;
                                }

                                extractedData = extractedData.substring(hashLength);
                            }

                            resolve(extractedData);

                        } catch (err) {
                            reject(err);
                        }
                    };
                    img.src = e.target.result;
                };

                reader.onerror = () => reject(new Error('Failed to read image file'));
                reader.readAsDataURL(imageFile);

            } catch (err) {
                reject(err);
            }
        });
    }

    static async fileToBinary(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const buffer = new Uint8Array(e.target.result);
                let binary = '';
                
                for (let i = 0; i < buffer.length; i++) {
                    binary += buffer[i].toString(2).padStart(8, '0');
                }
                
                const metadata = JSON.stringify({
                    name: file.name,
                    size: file.size,
                    mime: file.type
                });
                const metadataBinary = this.textToBinary(metadata);
                
                const magic = this.MAGIC;
                const sizeBinary = (binary.length >>> 0).toString(2).padStart(32, '0');
                const metaLenBinary = (metadataBinary.length >>> 0).toString(2).padStart(16, '0');
                
                resolve(magic + sizeBinary + metaLenBinary + metadataBinary + binary);
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsArrayBuffer(file);
        });
    }

    static binaryToFile(binary: string): { metadata: any, blob: Blob } | null {
        const magic = binary.substring(0, 16);
        if (magic !== this.MAGIC) return null;

        const fileSize = parseInt(binary.substring(16, 48), 2);
        const metadataLength = parseInt(binary.substring(48, 64), 2);
        
        const metadataBinary = binary.substring(64, 64 + metadataLength);
        const metadataJson = this.binaryToText(metadataBinary);
        
        let metadata = { name: 'unknown', size: 0, mime: 'application/octet-stream' };
        try {
            metadata = JSON.parse(metadataJson);
        } catch (e) {
            console.error('Failed to parse file metadata:', e);
            return null;
        }
        
        const dataStart = 64 + metadataLength;
        const dataBinary = binary.substring(dataStart, dataStart + fileSize);
        
        const bytes = [];
        for (let i = 0; i < dataBinary.length; i += 8) {
            bytes.push(parseInt(dataBinary.substr(i, 8), 2));
        }
        
        return {
            metadata: metadata,
            blob: new Blob([new Uint8Array(bytes)], { type: metadata.mime })
        };
    }
}

export class SteganoWidget extends HTMLElement {
    private mode: string | null = null;
    private message: string | null = null;
    private container!: HTMLDivElement;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.renderBase();
    }

    static get observedAttributes() {
        return ['mode', 'message'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (newValue) {
            if (name === 'mode') this.mode = newValue;
            if (name === 'message') this.message = decodeURIComponent(newValue);
            this.executeAction();
        }
    }

    renderBase() {
        this.shadowRoot!.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    max-width: 500px;
                    margin: 15px 0;
                    font-family: var(--font-family, system-ui, sans-serif);
                }
                .widget-card {
                    background: #0f172a;
                    border: 1px solid #1e293b;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.5);
                }
                .header {
                    background: #1e293b;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #fff;
                    border-bottom: 1px solid #334155;
                }
                .logo {
                    width: 24px;
                    height: 24px;
                    background: #38bdf8;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: bold;
                    color: #000;
                }
                .title {
                    font-weight: 600;
                    font-size: 14px;
                }
                .body {
                    padding: 16px;
                    color: #e2e8f0;
                }
                .input-group {
                    margin-bottom: 12px;
                }
                .input-group label {
                    display: block;
                    font-size: 12px;
                    font-weight: 600;
                    margin-bottom: 4px;
                    color: #94a3b8;
                }
                .input-group input[type="file"] {
                    display: block;
                    width: 100%;
                    font-size: 12px;
                }
                .input-group input[type="password"],
                .input-group textarea {
                    width: 100%;
                    padding: 8px;
                    background: #1e293b;
                    border: 1px solid #334155;
                    border-radius: 6px;
                    color: #fff;
                    font-size: 13px;
                    box-sizing: border-box;
                }
                .content-box {
                    background: #1e293b;
                    padding: 10px;
                    border-radius: 6px;
                    font-size: 13px;
                    line-height: 1.5;
                    border: 1px solid #334155;
                    white-space: pre-wrap;
                    margin-bottom: 12px;
                }
                .btn {
                    background: #38bdf8;
                    color: #0f172a;
                    border: none;
                    padding: 10px 16px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    width: 100%;
                    transition: opacity 0.2s;
                }
                .btn:hover {
                    opacity: 0.9;
                }
                .error { color: #f87171; font-size: 12px; margin-top: 10px; }
                .success { color: #4ade80; font-size: 12px; margin-top: 10px; }
            </style>
            <div class="widget-card">
                <div class="header">
                    <div class="logo"><i class="fas fa-lock"></i></div>
                    <div class="title" id="widgetTitle">SteganoCode Interface</div>
                </div>
                <div class="body" id="widgetBody">
                    Loading context...
                </div>
            </div>
        `;
        this.container = this.shadowRoot!.getElementById('widgetBody') as HTMLDivElement;
    }

    executeAction() {
        if (!this.mode) return;
        
        if (this.mode === 'embed') {
            this.renderEmbed();
        } else if (this.mode === 'decode') {
            this.renderDecode();
        }
    }

    renderEmbed() {
        const title = this.shadowRoot!.getElementById('widgetTitle')!;
        title.textContent = 'Embed Data into Image';

        let html = '';
        if (this.message) {
            html += `<div class="input-group"><label>Message to Embed</label><div class="content-box">${this.message}</div></div>`;
        } else {
            html += `<div class="input-group">
                <label>Text to Embed (Optional)</label>
                <textarea id="textPayload" rows="3" placeholder="Enter secret message..."></textarea>
            </div>`;
            html += `<div class="input-group">
                <label>OR File to Embed (Optional)</label>
                <input type="file" id="filePayload" />
            </div>`;
        }

        html += `
            <div class="input-group">
                <label>Cover Image (Required - Must be PNG or JPG)</label>
                <input type="file" id="coverImage" accept="image/png, image/jpeg" />
            </div>
            <div class="input-group">
                <label>Password (Optional)</label>
                <input type="password" id="password" placeholder="Encrypt with password..." />
            </div>
            <button class="btn" id="actionBtn">Embed & Download</button>
            <div id="statusMsg"></div>
        `;

        this.container.innerHTML = html;

        this.shadowRoot!.getElementById('actionBtn')!.addEventListener('click', () => this.handleEmbed());
    }

    renderDecode() {
        const title = this.shadowRoot!.getElementById('widgetTitle')!;
        title.textContent = 'Decode Hidden Data';

        this.container.innerHTML = `
            <div class="input-group">
                <label>Image to Decode</label>
                <input type="file" id="decodeImage" accept="image/png, image/jpeg" />
            </div>
            <div class="input-group">
                <label>Password (if encrypted)</label>
                <input type="password" id="decodePassword" placeholder="Enter password..." />
            </div>
            <button class="btn" id="actionBtn">Decode Image</button>
            <div id="statusMsg"></div>
            <div id="resultContainer" style="display:none; margin-top:10px;">
                <label style="font-size:12px; font-weight:600; color:#94a3b8; display:block; margin-bottom:4px;">Decoded Result:</label>
                <div class="content-box" id="resultBox"></div>
                <button class="btn" id="downloadFileBtn" style="display:none; background:#4ade80;">Download Hidden File</button>
            </div>
        `;

        this.shadowRoot!.getElementById('actionBtn')!.addEventListener('click', () => this.handleDecode());
    }

    async handleEmbed() {
        const statusMsg = this.shadowRoot!.getElementById('statusMsg')!;
        const btn = this.shadowRoot!.getElementById('actionBtn') as HTMLButtonElement;
        
        const coverInput = this.shadowRoot!.getElementById('coverImage') as HTMLInputElement;
        const passInput = this.shadowRoot!.getElementById('password') as HTMLInputElement;
        
        const coverImage = coverInput?.files?.[0];
        const password = passInput?.value || '';

        if (!coverImage) {
            statusMsg.className = 'error';
            statusMsg.textContent = 'Please select a cover image.';
            return;
        }

        btn.disabled = true;
        btn.textContent = 'Processing...';
        statusMsg.textContent = '';

        try {
            let binaryData = '';

            if (this.message) {
                // Use AI provided message
                binaryData = SteganoCodeCore.textToBinary(this.message);
            } else {
                const textInput = this.shadowRoot!.getElementById('textPayload') as HTMLTextAreaElement;
                const fileInput = this.shadowRoot!.getElementById('filePayload') as HTMLInputElement;
                const text = textInput?.value;
                const file = fileInput?.files?.[0];

                if (file) {
                    binaryData = await SteganoCodeCore.fileToBinary(file);
                } else if (text) {
                    binaryData = SteganoCodeCore.textToBinary(text);
                } else {
                    throw new Error('Please provide text or a file to embed.');
                }
            }

            const blob = await SteganoCodeCore.encodeImage(coverImage, binaryData, password);
            
            // Trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `encoded_${coverImage.name.split('.')[0]}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            statusMsg.className = 'success';
            statusMsg.textContent = 'Success! Image downloaded.';
        } catch (e: any) {
            statusMsg.className = 'error';
            statusMsg.textContent = e.message;
        } finally {
            btn.disabled = false;
            btn.textContent = 'Embed & Download';
        }
    }

    async handleDecode() {
        const statusMsg = this.shadowRoot!.getElementById('statusMsg')!;
        const btn = this.shadowRoot!.getElementById('actionBtn') as HTMLButtonElement;
        const resultContainer = this.shadowRoot!.getElementById('resultContainer')!;
        const resultBox = this.shadowRoot!.getElementById('resultBox')!;
        const downloadBtn = this.shadowRoot!.getElementById('downloadFileBtn') as HTMLButtonElement;
        
        const imgInput = this.shadowRoot!.getElementById('decodeImage') as HTMLInputElement;
        const passInput = this.shadowRoot!.getElementById('decodePassword') as HTMLInputElement;
        
        const image = imgInput?.files?.[0];
        const password = passInput?.value || '';

        if (!image) {
            statusMsg.className = 'error';
            statusMsg.textContent = 'Please select an image to decode.';
            return;
        }

        btn.disabled = true;
        btn.textContent = 'Decoding...';
        statusMsg.textContent = '';
        resultContainer.style.display = 'none';
        downloadBtn.style.display = 'none';
        
        // Remove old event listeners on downloadBtn if any
        const newDownloadBtn = downloadBtn.cloneNode(true) as HTMLButtonElement;
        downloadBtn.parentNode?.replaceChild(newDownloadBtn, downloadBtn);

        try {
            const binaryData = await SteganoCodeCore.decodeImage(image, password);
            
            const fileData = SteganoCodeCore.binaryToFile(binaryData);

            resultContainer.style.display = 'block';

            if (fileData) {
                // It's a file
                resultBox.textContent = `Hidden File Detected: ${fileData.metadata.name} (${(fileData.metadata.size/1024).toFixed(1)} KB)`;
                newDownloadBtn.style.display = 'block';
                newDownloadBtn.addEventListener('click', () => {
                    const url = URL.createObjectURL(fileData.blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileData.metadata.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                });
            } else {
                // It's plain text
                resultBox.textContent = SteganoCodeCore.binaryToText(binaryData);
            }

            statusMsg.className = 'success';
            statusMsg.textContent = 'Decoded successfully.';
        } catch (e: any) {
            statusMsg.className = 'error';
            statusMsg.textContent = e.message;
        } finally {
            btn.disabled = false;
            btn.textContent = 'Decode Image';
        }
    }
}

export function initSteganoBridge() {
    if (!customElements.get('stegano-widget')) {
        customElements.define('stegano-widget', SteganoWidget);
    }
}
